import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Hexagon, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

export default function MeetingRoom() {
  const { room } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(false);
  const containerRef = useRef(null);
  const jitsiRef = useRef(null);

  useEffect(() => {
    api
      .get(`/meetings/room/${room}`)
      .then((r) => {
        setMeeting(r.data);
        document.title = `${r.data.title} — Quantum Century`;
      })
      .catch(() => setError(true));
  }, [room]);

  useEffect(() => {
    if (!meeting || !containerRef.current) return;
    const start = () => {
      jitsiRef.current = new window.JitsiMeetExternalAPI("meet.jit.si", {
        roomName: `quantum-century-${meeting.room}`,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        configOverwrite: { prejoinPageEnabled: false, disableDeepLinking: true },
      });
    };
    if (window.JitsiMeetExternalAPI) {
      start();
    } else {
      const s = document.createElement("script");
      s.src = "https://meet.jit.si/external_api.js";
      s.onload = start;
      document.body.appendChild(s);
    }
    return () => jitsiRef.current?.dispose();
  }, [meeting]);

  if (error) {
    return (
      <div className="grid-bg flex min-h-screen flex-col items-center justify-center gap-4 bg-ink px-4 text-center" data-testid="meeting-not-found">
        <Hexagon className="h-12 w-12 text-cy" strokeWidth={1.2} />
        <h1 className="font-display text-2xl font-bold text-slate-100">Esta reunión ya no está disponible</h1>
        <p className="max-w-md text-sm text-slate-400">El enlace pudo haber sido retirado. Contacta a Quantum Century para recibir uno nuevo.</p>
        <Link to="/" className="mt-2 rounded-full bg-cy px-6 py-3 font-display text-sm font-bold text-ink transition-colors hover:bg-em">
          Ir a Quantum Century
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-ink" data-testid="meeting-room-page">
      <header className="flex items-center justify-between border-b border-cy/10 px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="relative grid h-8 w-8 place-items-center">
            <Hexagon className="h-8 w-8 text-cy" strokeWidth={1.4} />
            <span className="absolute font-display text-[10px] font-extrabold text-ink">Q</span>
          </span>
          <div className="leading-tight">
            <p className="font-display text-sm font-bold text-slate-100">{meeting ? meeting.title : "Cargando reunión…"}</p>
            <p className="font-mono text-[10px] tracking-[0.2em] text-slate-500">
              {meeting?.client_name ? `CON ${meeting.client_name.toUpperCase()} · ` : ""}QUANTUM CENTURY
            </p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 rounded-full border border-em/30 bg-em/10 px-3 py-1.5 font-mono text-[10px] text-em">
          <ShieldCheck className="h-3.5 w-3.5" /> Sala privada
        </span>
      </header>
      <div className="flex-1" ref={containerRef} data-testid="meeting-jitsi-container" />
      <p className="border-t border-cy/10 px-4 py-2 text-center font-mono text-[10px] tracking-[0.15em] text-slate-600">
        VIDEOLLAMADA SEGURA · QUANTUM CENTURY
      </p>
    </div>
  );
}
